/**
 * Predicates for filtering out known, unactionable noise in Datadog RUM.
 *
 * Each predicate identifies a specific source of browser-side errors that we
 * have deliberately chosen not to surface — typically because the error does
 * not originate from our code, lacks a usable stack trace, or is produced by
 * automated scanners/bots hitting our pages.
 *
 * Add a new predicate here when you identify another distinct noise pattern.
 * Keep predicates narrow and exact-shape; broad matches risk swallowing real
 * user errors.
 */

/**
 * Headless Chrome is the user-agent used by most link-preview scanners,
 * synthetic monitors, and scrapers. These sessions generate RUM events that
 * are never tied to a real user journey and only inflate noise.
 */
export const isHeadlessBot = (): boolean => navigator.userAgent.includes('HeadlessChrome')

/**
 * Cross-origin `"Script error."` is the browser's scrubbed error message when
 * a script from another origin throws without CORS headers. No stack, no file,
 * no line — completely unactionable.
 *
 * With `crossOrigin: 'anonymous'` in next.config.ts our own scripts now report
 * full details; only third-party scripts we can't control still produce this.
 *
 * The browser-rum SDK wraps non-Error throws with an `Uncaught ` prefix and
 * JSON.stringifies the raw string, producing `Uncaught "Script error."` — so
 * we match both the raw form and the SDK-wrapped form.
 */
export const isCrossOriginScriptError = (message: string | undefined): boolean =>
    message === 'Script error.' || (message?.startsWith('Uncaught "Script error.') ?? false)

/**
 * Microsoft Outlook Safe Links / Office link-preview scanners inject a script
 * into a hidden frame that calls `.update()` on an ActiveX-style host object
 * which only exists inside the Office desktop environment. On a normal web
 * page the object is missing, so Microsoft's own script throws and our origin
 * catches it.
 *
 * Always the exact shape:
 *     `Object Not Found Matching Id:<n>, MethodName:update, ParamCount:4`
 *
 * Volume correlates with email blasts, never with real users.
 */
export const isOutlookSafeLinksScanner = (message: string | undefined): boolean =>
    message?.includes('Object Not Found Matching Id:') ?? false

/**
 * Curated list of Google Identity Services (One Tap / FedCM) console messages we
 * have confirmed are benign, user-driven noise we cannot fix from our code: the
 * user dismisses the One Tap prompt, the browser aborts or declines the FedCM
 * request, the token fetch is cancelled, or a third-party-cookie / FedCM policy
 * blocks the flow.
 *
 * Messages matching this list are dropped outright (see client.ts beforeSend) —
 * they are confirmed-unuseful, so there is no reason to keep them. This is an
 * explicit allowlist of *known-unuseful* patterns, NOT a blanket `[GSI_LOGGER]`
 * match. An unrecognized GSI error is deliberately left at error level so it
 * still surfaces — e.g. `The given origin is not allowed for the given client
 * ID` is a real OAuth-client misconfiguration and is intentionally absent here.
 * The tradeoff is that a genuinely-new noise variant pages until we add it;
 * that's preferred over silently masking an unknown, possibly-real error. Add a
 * pattern only after confirming it's noise. Substrings, matched anywhere in the
 * message.
 */
const THIRD_PARTY_AUTH_NOISE_PATTERNS = [
    // Google — every FedCM get() rejection (AbortError "signal is aborted",
    // NetworkError "Error retrieving a token", "publicKey is not defined",
    // NotAllowedError "identity-credentials-get … not enabled", …).
    'FedCM get() rejects',
    // Google — credential-status polling that returns nothing usable.
    'Check credential status returns invalid response',
    // Google — failed XHRs to the GSI endpoint (no logger prefix).
    'accounts.google.com/gsi/',
]

export const isThirdPartyAuthNoise = (message: string | undefined): boolean => {
    if (!message) {
        return false
    }
    return THIRD_PARTY_AUTH_NOISE_PATTERNS.some((pattern) => message.includes(pattern))
}

/**
 * The Amplitude browser SDK logs a `console.error` for every analytics request
 * it can't complete — `Amplitude Logger [Error]: Load failed` (Safari) or
 * `Amplitude Logger [Error]: Failed to fetch` (Chromium). These are
 * overwhelmingly ad/tracker blockers blocking the first-party `an.wanderu.com`
 * (events) and `an-config.wanderu.com` (remote-config) beacons: fire-and-forget,
 * not actionable from our code, and the dominant client log-error source. The
 * paired status-0 network log is dropped by host (see client.ts beforeSend);
 * this drops the SDK's own console line.
 *
 * Narrow on purpose: only the two blocked-fetch phrasings, and only when carrying
 * the SDK's `Amplitude Logger` tag — so genuine SDK errors (e.g. `No remote
 * config received`, init failures) still surface at error.
 */
export const isAmplitudeFetchNoise = (message: string | undefined): boolean => {
    if (!message?.startsWith('Amplitude Logger')) {
        return false
    }
    return message.includes('Load failed') || message.includes('Failed to fetch')
}

/**
 * Next.js App Router fires RSC prefetch / navigation GETs carrying a `?_rsc=`
 * marker. When the user hovers or navigates away, Next aborts the in-flight
 * prefetch and the fetch reports `status_code: 0` — expected cancellation, not a
 * failure. A genuine RSC failure returns a real 4xx/5xx status and still surfaces.
 * Volume spikes with traffic surges (e.g. email blasts) as prefetches are started
 * and cancelled en masse. Narrow: status 0 + GET + the `_rsc=` marker only.
 */
export const isAbortedRscPrefetch = (
    method: string | undefined,
    status: number | undefined,
    url: string | undefined,
): boolean => status === 0 && method === 'GET' && (url?.includes('_rsc=') ?? false)
